import { useCallback, useEffect, useState } from 'react';
import { Check, LockKeyhole, LogOut, Pencil, ShieldCheck, Truck, X } from 'lucide-react';
import type { AuthSession, Device, User } from '../../types/user';
import {
  ALL_PERMISSIONS,
  PERMISSION_LABELS,
  ROLE_LABELS,
  canSeeRoleBadge,
  hasPermission,
  permissionsFor,
} from '../../core/permissions';
import { shortFingerprint } from '../../core/device';
import { formatPhone } from '../../core/phone';
import { approveDevice, getDevice, listPendingDevices, listUsers } from '../../db/auth-store';
import { ProfileModal } from '../settings/ProfileModal';

interface SignedInPanelProps {
  user: User;
  session: AuthSession;
  /** Keeps the session but returns to the PIN screen. */
  onLock: () => void;
  onSignOut: () => void;
  /** The operator edited their own details — the whole shell follows them. */
  onProfileUpdated?: (user: User) => void;
}

/**
 * Post-login shell for Task 2: proves the session, shows the effective
 * permission matrix and lets a super-admin approve pending devices.
 * The operational screens (tasks, vehicles, teams) arrive in a later task.
 */
export function SignedInPanel({
  user,
  session,
  onLock,
  onSignOut,
  onProfileUpdated,
}: SignedInPanelProps) {
  const [device, setDevice] = useState<Device | null>(null);
  const [pending, setPending] = useState<Device[]>([]);
  const [names, setNames] = useState<Record<string, string>>({});
  const [editingProfile, setEditingProfile] = useState(false);

  const granted = new Set(permissionsFor(user));
  const canApprove = hasPermission(user, 'APPROVE_DEVICE');

  const loadPending = useCallback(async () => {
    if (!canApprove) return;
    const [devices, users] = await Promise.all([listPendingDevices(), listUsers()]);
    setPending(devices);
    setNames(Object.fromEntries(users.map((entry) => [entry.id, entry.fullName])));
  }, [canApprove]);

  useEffect(() => {
    void (async () => {
      setDevice(await getDevice(session.deviceId));
      await loadPending();
    })();
  }, [session.deviceId, loadPending]);

  const handleApprove = async (deviceId: string) => {
    await approveDevice(user, deviceId);
    await loadPending();
  };

  // The application shell owns the page padding; this is only the content.
  return (
    <div className="mx-auto w-full max-w-2xl space-y-4">
        <section className="bg-panel border border-line rounded-2xl shadow-sm p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-brand-50 text-brand-500 flex items-center justify-center">
                <Truck size={22} aria-hidden />
              </div>
              <div>
                <h1 className="text-lg font-bold">{user.fullName}</h1>
                <p className="text-sm text-ink/60">
                  {canSeeRoleBadge(user) ? `${ROLE_LABELS[user.role]} · ` : ''}
                  {user.company} · {user.platoon}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              {/* Every account owns its own name, phone and display choices —
                  including the levels that never see the settings screen this
                  used to be reached from. */}
              <button
                type="button"
                onClick={() => setEditingProfile(true)}
                title="עריכת פרטים אישיים"
                className="w-10 h-10 rounded-xl border border-line flex items-center justify-center text-ink/60 hover:text-ink"
              >
                <Pencil size={18} aria-hidden />
              </button>
              <button
                type="button"
                onClick={onLock}
                title="נעילה"
                className="w-10 h-10 rounded-xl border border-line flex items-center justify-center text-ink/60 hover:text-ink"
              >
                <LockKeyhole size={18} aria-hidden />
              </button>
              <button
                type="button"
                onClick={onSignOut}
                title="יציאה"
                className="w-10 h-10 rounded-xl border border-line flex items-center justify-center text-ink/60 hover:text-ink"
              >
                <LogOut size={18} aria-hidden />
              </button>
            </div>
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-ink/50">טלפון</dt>
              <dd data-dir="ltr">{formatPhone(user.phone)}</dd>
            </div>
            <div>
              <dt className="text-ink/50">מספר אישי</dt>
              <dd data-dir="ltr">{user.personalNumber}</dd>
            </div>
            <div>
              <dt className="text-ink/50">מכשיר</dt>
              <dd>{device?.deviceName ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-ink/50">טביעת אצבע</dt>
              <dd className="font-mono text-xs" data-dir="ltr">
                {shortFingerprint(session.deviceFingerprint)}
              </dd>
            </div>
          </dl>
        </section>

        <section className="bg-panel border border-line rounded-2xl shadow-sm p-5 sm:p-6">
          <h2 className="font-semibold mb-1 flex items-center gap-2">
            <ShieldCheck size={18} className="text-brand-500" aria-hidden />
            מטריצת הרשאות
          </h2>
          <p className="text-sm text-ink/60 mb-4">
            {granted.size} מתוך {ALL_PERMISSIONS.length} הרשאות פעילות
          </p>
          <ul className="grid sm:grid-cols-2 gap-2">
            {ALL_PERMISSIONS.map((action) => {
              const allowed = granted.has(action);
              return (
                <li
                  key={action}
                  className={[
                    'flex items-center gap-2 rounded-xl border p-2.5 text-sm',
                    allowed ? 'border-brand-100 bg-brand-50' : 'border-line bg-canvas text-ink/40',
                  ].join(' ')}
                >
                  {allowed ? (
                    <Check size={16} className="text-brand-700 shrink-0" aria-hidden />
                  ) : (
                    <X size={16} className="shrink-0" aria-hidden />
                  )}
                  <span>{PERMISSION_LABELS[action]}</span>
                </li>
              );
            })}
          </ul>
        </section>

        {canApprove ? (
          <section className="bg-panel border border-line rounded-2xl shadow-sm p-5 sm:p-6">
            <h2 className="font-semibold mb-3">מכשירים הממתינים לאישור</h2>
            {pending.length === 0 ? (
              <p className="text-sm text-ink/60">אין בקשות ממתינות.</p>
            ) : (
              <ul className="space-y-2">
                {pending.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-line p-3"
                  >
                    <div className="text-sm">
                      <p className="font-medium">{names[entry.userId] ?? entry.userId}</p>
                      <p className="text-ink/60">
                        {entry.deviceName} ·{' '}
                        <span className="font-mono text-xs" data-dir="ltr">
                          {shortFingerprint(entry.deviceFingerprint)}
                        </span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleApprove(entry.id)}
                      className="h-9 px-4 rounded-lg bg-brand-500 text-white text-sm font-medium hover:bg-brand-700"
                    >
                      אישור
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {editingProfile ? (
          <ProfileModal
            user={user}
            onClose={() => setEditingProfile(false)}
            onSaved={(updated) => onProfileUpdated?.(updated)}
          />
        ) : null}
    </div>
  );
}
